import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { gradeLabel, canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Printer, Send, FileDown, ShieldCheck, Link2, Copy, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";

export default function SARBuilder() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [themes, setThemes] = useState([]);
  const [current, setCurrent] = useState(searchParams.get("theme") || "safeguarding");
  const [actions, setActions] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [comments, setComments] = useState([]);
  const [narrative, setNarrative] = useState("");
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [signoffs, setSignoffs] = useState([]);
  const [shares, setShares] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const loadThemes = async () => { const r = await api.get("/themes"); setThemes(r.data); };
  const loadTheme = async () => {
    const [a, e, c, s] = await Promise.all([
      api.get(`/actions?theme_key=${current}`),
      api.get(`/evidence?theme_key=${current}`),
      api.get(`/comments?theme_key=${current}`),
      api.get(`/signoffs?theme_key=${current}`),
    ]);
    setActions(a.data); setEvidence(e.data); setComments(c.data); setSignoffs(s.data);
    const t = themes.find((x) => x.key === current);
    setNarrative(t?.narrative || "");
  };

  useEffect(() => { loadThemes(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (themes.length) loadTheme(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [current, themes]);
  useEffect(() => { setSearchParams({ theme: current }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [current]);

  const theme = themes.find((t) => t.key === current);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/sar/generate", { theme_key: current });
      setNarrative(data.narrative);
      toast.success("SAR narrative drafted");
      loadThemes();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setGenBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/themes/${current}`, { narrative });
      toast.success("Narrative saved");
      loadThemes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const postComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post("/comments", { theme_key: current, text: commentText });
      setCommentText(""); loadTheme();
    } catch (e) { toast.error("Failed"); }
  };

  const approve = async () => {
    try { await api.post("/signoffs", { theme_key: current, decision: "approved", comment: "Signed off from SAR Builder" });
      toast.success("Section approved & locked"); loadTheme();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("qip_token");
    window.open(`${api.defaults.baseURL}/sar/pdf?auth=${token}`, "_blank");
  };

  const createShare = async () => {
    try {
      const { data } = await api.post("/share", { label: "Inspector read-only", days_valid: 7 });
      const url = `${window.location.origin}/share/${data.token}`;
      setShareUrl(url); setShareOpen(true);
      toast.success("Inspector link generated (7 days)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyShare = () => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); };

  if (!theme) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4 no-print">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">SAR Builder</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Self-assessment, evidence-led</h1>
          <p className="text-slate-600 mt-1">Draft narratives for each Ofsted area, pulling directly from QIP actions and evidence.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
        <Button onClick={() => window.print()} variant="outline" data-testid="print-sar"><Printer className="h-4 w-4 mr-2" />Print</Button>
        <Button onClick={downloadPdf} variant="outline" data-testid="pdf-sar"><FileDown className="h-4 w-4 mr-2" />Download PDF</Button>
        {canManage(user?.role) && <Button onClick={createShare} variant="outline" data-testid="share-sar"><Link2 className="h-4 w-4 mr-2" />Inspector link</Button>}
        </div>
      </div>

      <Tabs value={current} onValueChange={setCurrent} className="no-print">
        <TabsList className="bg-white border border-slate-200 h-auto flex-wrap p-1">
          {themes.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`sar-tab-${t.key}`} className="text-xs data-[state=active]:bg-indigo-900 data-[state=active]:text-white">{t.title}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Card className="p-6 border-slate-200">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{theme.code}</div>
                <h2 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{theme.title}</h2>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">{theme.description}</p>
              </div>
              <Badge className={`grade-${theme.current_grade} border`}>{gradeLabel(theme.current_grade)}</Badge>
            </div>

            <div className="mt-6 flex items-center gap-2 no-print">
              {canManage(user?.role) && (
                <Button data-testid="ai-draft-btn" onClick={generate} disabled={genBusy} className="bg-indigo-900 hover:bg-indigo-950 text-white">
                  <Sparkles className="h-4 w-4 mr-2" />{genBusy ? "Drafting with Claude…" : "AI draft narrative (Claude Sonnet 5)"}
                </Button>
              )}
              {canManage(user?.role) && <Button data-testid="save-narrative-btn" onClick={save} disabled={busy} variant="outline">{busy ? "Saving…" : "Save narrative"}</Button>}
              {canManage(user?.role) && <Button data-testid="approve-btn" onClick={approve} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><ShieldCheck className="h-4 w-4 mr-2" />Approve section</Button>}
            </div>

            {signoffs.length > 0 && (
              <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                <b>{signoffs.length} approval{signoffs.length === 1 ? "" : "s"}:</b>{" "}
                {signoffs.slice(0, 2).map((s) => `${s.approver_name} · ${new Date(s.created_at).toLocaleDateString("en-GB")}`).join(" · ")}
              </div>
            )}

            <div className="mt-4">
              <Textarea data-testid="sar-narrative" value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={18} placeholder="Draft your SAR narrative here, or click 'AI draft narrative' to pull from linked actions and evidence…" className="font-serif text-[15px] leading-relaxed" />
            </div>
          </Card>

          <Card className="p-6 border-slate-200 no-print">
            <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Review comments</h3>
            <div className="space-y-3 mb-4">
              {comments.length === 0 && <div className="text-sm text-slate-500">No comments yet.</div>}
              {comments.map((c) => {
                const canDeleteC = c.author_id === user?.id || ["admin", "quality_lead"].includes(user?.role);
                const delComment = async () => {
                  if (!confirm("Delete this comment?")) return;
                  try { await api.delete(`/comments/${c.id}`); toast.success("Deleted"); loadTheme(); }
                  catch (e) { toast.error("Failed"); }
                };
                return (
                  <div key={c.id} className="border-l-2 border-indigo-200 pl-3 py-1 group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-xs text-slate-500"><b className="text-slate-800">{c.author_name}</b> · {c.author_role} · {new Date(c.created_at).toLocaleString("en-GB")}</div>
                      {canDeleteC && <button data-testid={`del-comment-${c.id}`} onClick={delComment} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-700 transition-opacity"><X className="h-3.5 w-3.5" /></button>}
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">{c.text}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Input data-testid="comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a review comment…" />
              <Button data-testid="comment-post" onClick={postComment} className="bg-indigo-900 hover:bg-indigo-950 text-white"><Send className="h-4 w-4" /></Button>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <Card className="p-5 border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Plus Jakarta Sans" }}>Linked QIP actions ({actions.length})</h3>
            <div className="space-y-2 max-h-72 overflow-auto">
              {actions.length === 0 && <div className="text-sm text-slate-500">No actions linked yet.</div>}
              {actions.map((a) => (
                <div key={a.id} className="text-sm border border-slate-100 rounded-md p-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full rag-dot-${a.rag_status}`} />
                    <div className="font-medium text-slate-900 flex-1">{a.title}</div>
                    <span className="text-[11px] font-mono text-slate-500">{a.progress || 0}%</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Owner: {a.owner || "—"}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5 border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Plus Jakarta Sans" }}>Linked evidence ({evidence.length})</h3>
            <div className="space-y-2 max-h-72 overflow-auto">
              {evidence.length === 0 && <div className="text-sm text-slate-500">No evidence linked yet.</div>}
              {evidence.map((e) => (
                <div key={e.id} className="text-sm border border-slate-100 rounded-md p-2.5">
                  <div className="font-medium text-slate-900">{e.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{e.source_type} · by {e.created_by_name}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-white">
          <div className="text-lg font-bold mb-2" style={{ fontFamily: "Plus Jakarta Sans" }}>Inspector read-only link</div>
          <p className="text-sm text-slate-600 mb-3">Share this link with inspectors or governors. It expires in 7 days and bundles all seven themes, actions, evidence and sign-offs. No login required.</p>
          <div className="flex gap-2"><Input data-testid="share-url" value={shareUrl} readOnly /><Button onClick={copyShare} data-testid="copy-share" className="bg-indigo-900 hover:bg-indigo-950 text-white"><Copy className="h-4 w-4" /></Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
